import React, { useState, useRef, useEffect } from 'react';

export interface DropdownMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface DropdownMenuProps {
  trigger: React.ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  className?: string;
}

/**
 * DropdownMenu Component v2 - FacturXPro Design System
 *
 * Menu déroulant d'actions avec items configurables
 * Utilisé pour les menus d'actions (Edit, Delete, etc.)
 *
 * @example
 * ```tsx
 * <DropdownMenu
 *   trigger={<IconButton variant="gray"><MoreVertical /></IconButton>}
 *   items={[
 *     { label: 'Modifier', icon: <Edit />, onClick: handleEdit },
 *     { label: 'Dupliquer', icon: <Copy />, onClick: handleDuplicate },
 *     { label: 'Supprimer', icon: <Trash />, onClick: handleDelete, variant: 'danger' },
 *   ]}
 *   align="right"
 * />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Dropdown Menu"
 */
export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  items,
  align = 'right',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: DropdownMenuItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>

      {isOpen && (
        <div
          className={`
            absolute
            top-full
            ${align === 'right' ? 'right-0' : 'left-0'}
            mt-2
            w-56
            bg-white
            border
            border-gray-200
            rounded-lg
            shadow-lg
            py-1
            z-50
            animate-fade-in-up
          `.trim()}
          style={{ animationDuration: '150ms' }}
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
              className={`
                w-full
                flex
                items-center
                gap-3
                px-4
                py-2.5
                text-sm
                font-medium
                text-left
                transition-colors
                ${
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : item.variant === 'danger'
                    ? 'text-red-700 hover:bg-red-50'
                    : 'text-gray-900 hover:bg-gray-50'
                }
              `.trim()}
            >
              {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
